import { AddIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import PageCard from 'components/page-card';
import SortIndicator from 'components/table/SortIndicator';
import ErrorState from '../../../components/states/ErrorState';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { translate } from '../../../i18n';
import { UserCohortDto } from '../../../models/user-cohort/dto/user-cohort.dto';
import { tableHeaderTextProps, tableProps, tableThProps, tableThFlexProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useDeleteCohort } from '../hooks/useDeleteCohort';
import { useGetCohorts } from '../hooks/useGetCohorts';
import CreateCohortModal from './CreateCohortModal';

const columnHelper = createColumnHelper<UserCohortDto>();

export default function CohortList() {
  const { selectedOrganization } = useSelectedOrganization();
  const {
    data: cohorts,
    isInitialLoading,
    isError,
  } = useGetCohorts(selectedOrganization?.id);
  // `useGetCohorts` is `enabled: Boolean(organizationId)`, and a disabled query
  // reports `isLoading: true` forever — so the raw flag this used to read made
  // the whole page the word "Loading…" for any session with no organization
  // selected, which run 100 measured as reachable on every admin page.
  const readState = classifyRead(isError, isInitialLoading, cohorts);
  const deleteCohort = useDeleteCohort(selectedOrganization?.id || '');
  const navigate = useNavigate();
  const toast = useToast();

  const [sorting, setSorting] = useState<SortingState>([]);
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const {
    isOpen: isCreateOpen,
    onOpen: onCreateOpen,
    onClose: onCreateClose,
  } = useDisclosure();

  const handleDelete = async (cohortId: string, cohortName: string) => {
    if (
      window.confirm(
        `${translate('cohort.confirm_delete')} "${cohortName}"?`,
      )
    ) {
      try {
        await deleteCohort.mutateAsync(cohortId);
        toast({
          title: translate('cohort.deleted_successfully'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: translate('cohort.delete_failed'),
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      id: 'name',
      header: () => (
        <Text
          {...tableHeaderTextProps}
        >
          {translate('cohort.name')}
        </Text>
      ),
      cell: (info) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('description', {
      id: 'description',
      header: () => (
        <Text
          {...tableHeaderTextProps}
        >
          {translate('cohort.description')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" noOfLines={2}>
          {info.getValue() || '-'}
        </Text>
      ),
    }),
    columnHelper.accessor('memberCount', {
      id: 'memberCount',
      header: () => (
        <Text
          {...tableHeaderTextProps}
        >
          {translate('cohort.members')}
        </Text>
      ),
      cell: (info) => (
        <Badge colorScheme="brand" fontSize="sm">
          {info.getValue() || 0} {translate('cohort.members_count')}
        </Badge>
      ),
    }),
    columnHelper.accessor('createdAt', {
      id: 'createdAt',
      header: () => (
        <Text
          {...tableHeaderTextProps}
        >
          {translate('cohort.created_at')}
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm">
          {new Date(info.getValue()).toLocaleDateString()}
        </Text>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: () => (
        <Text
          {...tableHeaderTextProps}
        >
          {translate('cohort.actions')}
        </Text>
      ),
      cell: (info) => (
        <Flex gap={2}>
          <IconButton
            aria-label={translate('cohort.edit_cohort')}
            icon={<EditIcon />}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/admin/user-management/cohorts/${info.row.original.id}`);
            }}
          />
          <IconButton
            aria-label={translate('cohort.delete_cohort')}
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(info.row.original.id, info.row.original.name);
            }}
          />
        </Flex>
      ),
    }),
  ], [textColor, navigate, handleDelete]);

  const table = useReactTable({
    data: cohorts || [],
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (readState === 'never-issued') {
    return (
      <PageCard title={translate('cohort.cohorts')}>
        <ScopePrompt gate='organization' />
      </PageCard>
    );
  }

  if (readState === 'loading') {
    return (
      <PageCard title={translate('cohort.loading')} />
    );
  }

  if (isError) {
    return (
      <PageCard title={translate('cohort.cohorts')}>
        <ErrorState message={translate('cohort.error_loading')} />
      </PageCard>
    );
  }

  return (
    <>
      <PageCard
        title={translate('cohort.cohorts')}
        rightHeader={
          <Button
            leftIcon={<AddIcon />}
            colorScheme="brand"
            size="md"
            onClick={onCreateOpen}
          >
            {translate('cohort.create_cohort')}
          </Button>
        }
      >

        {cohorts && cohorts.length === 0 ? (
          <Flex
            px="25px"
            py="50px"
            justifyContent="center"
            align="center"
            flexDirection="column"
          >
            <Text color="gray.500" fontSize="lg" mb={4}>
              {translate('cohort.no_cohorts')}
            </Text>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="brand"
              onClick={onCreateOpen}
            >
              {translate('cohort.create_first_cohort')}
            </Button>
          </Flex>
        ) : (
          <Box>
            <Table {...tableProps}>
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        {...tableThProps}
                        borderColor={borderColor}
                        cursor="pointer"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Flex
                          {...tableThFlexProps}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                          <SortIndicator direction={header.column.getIsSorted()} />
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {table.getRowModel().rows.map((row) => (
                  <Tr
                    key={row.id}
                    {...tableRowProps}
                    onClick={() => {
                      navigate(`/admin/user-management/cohorts/${row.original.id}`);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        {...tableCellProps}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </PageCard>

      <CreateCohortModal isOpen={isCreateOpen} onClose={onCreateClose} />
    </>
  );
}
